import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `rows` (default): header + bordered rows, overflow hidden. `padded`: 16 px padding, 18 px gap column. */
  mode?: 'rows' | 'padded';
  gap?: 'sm' | 'md' | 'lg';
}

/** Panel surface: bg panel, 1 px line border, radius 10 desktop / 12 mobile. */
export function Card({ mode = 'rows', gap = 'lg', className, children, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-2xl border border-line bg-panel md:rounded-xl',
        mode === 'rows' && 'overflow-hidden',
        mode === 'padded' && 'flex flex-col p-4',
        mode === 'padded' && gap === 'lg' && 'gap-[18px]',
        mode === 'padded' && gap === 'md' && 'gap-4',
        mode === 'padded' && gap === 'sm' && 'gap-2.5',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  /** Mono meta on the right (counts, states). */
  meta?: ReactNode;
  metaTone?: 'fg4' | 'accent' | 'ok' | 'warn';
  /** Subtitle under the title (mono 10px fg4). */
  hint?: ReactNode;
  actions?: ReactNode;
  /** 14/16 padding (list cards) or 12/16 (dense). Mobile uses 14 16 10. */
  dense?: boolean;
  className?: string;
}

export function CardHeader({
  title,
  meta,
  metaTone = 'fg4',
  hint,
  actions,
  dense,
  className,
}: CardHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 px-4 pb-2.5 pt-3.5',
        dense ? 'md:py-3' : 'md:py-3.5',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-[5px]">
        <span className="text-md font-semibold leading-none">{title}</span>
        {hint && <span className="font-mono text-xs font-normal text-fg4">{hint}</span>}
      </div>
      {actions}
      {meta !== undefined && !actions && (
        <span
          className={cn(
            'meta',
            metaTone === 'fg4'
              ? 'text-fg4'
              : metaTone === 'accent'
                ? 'text-accent'
                : metaTone === 'ok'
                  ? 'text-ok'
                  : 'text-warn',
          )}
        >
          {meta}
        </span>
      )}
    </div>
  );
}

/** Card title for padded cards: 500 13px/1. */
export function CardTitle({
  children,
  meta,
  className,
}: {
  children: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center justify-between', className)}>
      <span className="text-md font-medium leading-none">{children}</span>
      {meta && <span className="meta text-fg4">{meta}</span>}
    </div>
  );
}

/** Full-width footer link row (ALL ALERTS →). */
export function CardFooterLink({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full border-0 border-t border-line bg-transparent px-4 py-3 text-left font-mono text-sm font-medium tracking-meta text-fg3 hover:text-fg',
        className,
      )}
    >
      {children}
    </button>
  );
}
